import { useCallback, useState } from 'react';
import { REQUEST_TYPES } from '@demandproai/shared';
import useAxios from './useAxios';
import Constants from '../Constants';
import { toaster } from '../utils/toast';

const hasDemandAnchor = (list) => list.some((r) => r?.requestType === REQUEST_TYPES.DEMAND);

const errorMessage = 'Failed to load case data.';
const DEFAULT_PAGE_SIZE = 5;

const useCaseResultsHistory = ({ caseId, pageSize = DEFAULT_PAGE_SIZE } = {}) => {
  const { getData } = useAxios();
  const [caseData, setCaseData] = useState(null);
  const [createRequests, setCreateRequests] = useState([]);
  const [hasMoreHistory, setHasMoreHistory] = useState(true);

  // pure network read: fetches a page and returns it WITHOUT touching React state,
  // so callers (like the undo history walk) can loop over pages free of re-render races
  const fetchHistoryPage = useCallback(async (page) => {
    if (!caseId) return null;
    const params = new URLSearchParams({ page, pageSize });
    try {
      const response = await getData(
        `${Constants.ApiUrl.case.getCaseResultsHistory}/${caseId}?${params.toString()}`
      );
      if (!response?.success) {
        toaster({ message: response?.message || errorMessage, success: false });
        return null;
      }
      const results = response.data.createRequests || [];
      // fewer results than requested means we've reached the beginning of history
      return { caseData: response.data.caseData, createRequests: results, hasMore: results.length >= pageSize };
    } catch (error) {
      toaster({ message: error?.message || errorMessage, success: false });
      return null;
    }
  }, [caseId, pageSize, getData]);

  const fetchCaseDetails = useCallback(async () => {
    const data = await fetchHistoryPage(1);
    if (data) {
      setCaseData(data.caseData);
      setCreateRequests(data.createRequests);
      setHasMoreHistory(data.hasMore && !hasDemandAnchor(data.createRequests));
    }
    return data;
  }, [fetchHistoryPage]);

  // walks older pages purely in local scope (no state writes mid-loop) until isSatisfied(all) or history ends,
  // then commits the accumulated list once; returns the full accumulated array for the caller to act on
  const loadOlderUntil = useCallback(async (isSatisfied) => {
    let accumulated = [];
    let page = 0;
    let hasMore = true;
    while (hasMore && !hasDemandAnchor(accumulated) && !isSatisfied(accumulated)) {
      page += 1;
      const data = await fetchHistoryPage(page);
      if (!data) break;
      accumulated = [...data.createRequests, ...accumulated];
      hasMore = data.hasMore;
      if (page === 1 && data.caseData) setCaseData(data.caseData);
    }
    setCreateRequests(accumulated);
    setHasMoreHistory(hasMore && !hasDemandAnchor(accumulated));
    return accumulated;
  }, [fetchHistoryPage]);

  return {
    caseData,
    createRequests,
    pageSize,
    hasMoreHistory,
    fetchCaseDetails,
    loadOlderUntil,
  };
};

export default useCaseResultsHistory;
